import { MotionValue, motion, useScroll, useTransform } from "framer-motion";
import React, { useEffect, useRef, useState } from "react";
import Lenis from 'lenis';
import Container from "components/Container";
import { experiences } from "data";
import Paragraph from "components/Paragraph";
import Heading from "components/Heading";

type ExperienceType = {
    company: string;
    position: string;
    year: string;
    desc: string;
}

export default function Experience() {
    const container = useRef<HTMLElement | null>(null);
    const { scrollYProgress } = useScroll({
        target: container,
        offset: ["start start", "end end"],
    });

    useEffect(() => {
        const lenis = new Lenis();

        function raf(time: number) {
            lenis.raf(time);
            requestAnimationFrame(raf);
        }

        requestAnimationFrame(raf);
    }, []);

    return (
        <section ref={container} className="relative h-[800px] md:h-[2400px] lg:h-[1600px]  xl:h-[2000px] 2xl:h-[200vh]">
            <Section1 scrollYProgress={scrollYProgress} />
            <Section2 scrollYProgress={scrollYProgress} />
        </section>
    );
}
const Section1 = ({ scrollYProgress }: { scrollYProgress: MotionValue<number> }) => {
    const [windowWidth, setWindowWidth] = useState(typeof window === 'undefined' ? 0 : window.innerWidth);

    useEffect(() => {
        const handleResize = () => setWindowWidth(typeof window === 'undefined' ? 0 : window.innerWidth);
        window.addEventListener("resize", handleResize);
        return () => window.removeEventListener("resize", handleResize);
    }, []);

    const scale = useTransform(scrollYProgress, [0, 1], windowWidth > 767 ? [1, 0.8] : [1, 1]);
    const rotate = useTransform(scrollYProgress, [0, 1], windowWidth > 767 ? [0, -5] : [0, 0]);
    const border = useTransform(scrollYProgress, [0, 1], windowWidth > 767 ? ["0px solid #F9941A", "12px solid #F9941A"] : ["0px solid #F9941A", "0px solid #F9941A"]);

    return (
        <motion.div style={{ scale, rotate, border }} className="sticky top-0 h-[800px] md:h-[1200px] lg:h-[800px]  xl:h-[1000px] 2xl:h-[100vh] bg-[#FFA98F] pb-[10vh]">
            <Container className="my-auto relative">
                <Heading className="text-smoke py-8 border-b-2 border-smoke mb-4 4xl:mb-8">EXPERIENCE</Heading>
                {experiences.map((experience: ExperienceType, index: number) => (
                    <div key={index} className="mb-4 md:w-[720px] lg:w-[940px] xl:w-[1000px] 2xl:w-[1200px] 4xl:w-[2000px] border-b border-smoke ">
                        <div className="flex flex-row justify-between text-smoke">
                            <div className="flex flex-col">
                                <h3 className="text-xl md:text-5xl md:leading-tight lg:text-4xl 2xl:text-5xl uppercase">{experience?.company}</h3>
                                <p className="text-lg md:text-2xl 4xl:text-3xl 4xl:mb-2 uppercase font-ibm ">{experience?.position}</p>
                            </div>
                            <div className="bg-lemone mb-auto flex justify-center items-center">
                                <p className="font-medium text-smoke text-xs md:text-xl 4xl:text-4xl font-ibm">{experience?.year}</p>
                            </div>
                        </div>
                        <Paragraph className="text-smoke text-sm mb-4 4xl:mb-6">
                            {experience?.desc}
                        </Paragraph>
                    </div>
                ))}
            </Container>
        </motion.div>
    );
};

const Section2 = ({ scrollYProgress }: { scrollYProgress: MotionValue<number> }) => {
    const scale = useTransform(scrollYProgress, [0, 1], [0.8, 1]);
    const rotate = useTransform(scrollYProgress, [0, 1], [5, 0]);

    return (
        <motion.div style={{ scale, rotate }} className="relative bg-smoke h-[800px] md:h-[1200px] lg:h-[800px] xl:h-[1000px] 2xl:h-[100vh] hidden md:block">
            <div className="mx-center flex items-center justify-center h-full">
                <h2 className="text-6xl lg:text-9xl font-nebulica font-semibold text-[#fff] p-[7px] lg:p-[15px] !pr-0 border lg:border-[2px] border-lemone">YOU<span className="bg-lemone text-smoke">TUBE</span></h2>
            </div>
        </motion.div>
    );
};